import { useState, useEffect } from 'react'
import { getAllCardByUserId } from '../services/cards'
import { useAuth } from '../modules/auth'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { addHomeCardData, updateHomeCardData } from "../store/slice/card-slice"

const useGetHomeCards = (): any => {
  const dispatch: Dispatch<any> = useDispatch()
  const [allCards, setAllCards] = useState<any>([])
  const [homeFetchCardLoader, setHomeFetchCardLoader] = useState<any>(false)
  const [doneColumnLoader, setDoneColumnLoader] = useState<any>(false)
  const { currentUser } = useAuth()
  const { id, fullName } = currentUser?.data.user
  // Redux mirror: the homepage's local `allCards` is a snapshot from
  // the last `cardsHandler` fetch. Targeted upserts elsewhere (e.g.
  // the checklist assignee sync via upsertCurrentUserCard) update the
  // redux slice but would otherwise be invisible here. Mirror by id +
  // assignee-of-current-user so cards appear/update without a reload.
  const reduxAllCurrentUserCards = useSelector(
    (state: any) => state.cardReducer.allCurrentUserCards
  )
  const cardsHandler = async (cardsStatus: string, dateFilters: any, colType?: any) => {
    try {
      if (colType === 'done') { setDoneColumnLoader(true) }
      else { setHomeFetchCardLoader(true) }
      const response: any = await getAllCardByUserId(id, cardsStatus, dateFilters)
      if (response && response.data) {
        setAllCards(response.data.data);
        dispatch(addHomeCardData(response.data.data));
        dispatch(updateHomeCardData(response.data.data));
        if (colType === 'done') { setDoneColumnLoader(false) }
        else { setHomeFetchCardLoader(false) }

      }
    } catch (error) {
      return error
    }
  }

  useEffect(() => {
    if (!Array.isArray(reduxAllCurrentUserCards) || reduxAllCurrentUserCards.length === 0) return
    // Cards on the homepage are always those the current user is an
    // assignee on. `assignees` shape varies (string[] of fullNames or
    // object[] with fullName field) — handle both.
    const isAssignedToMe = (card: any): boolean => {
      const list = card?.assignees
      if (!Array.isArray(list) || list.length === 0) return false
      const meLower = (fullName ?? '').trim().toLowerCase()
      if (!meLower) return false
      return list.some((a: any) => {
        const name =
          typeof a === 'string' ? a : (a?.fullName ?? a?.name ?? '')
        return name.trim().toLowerCase() === meLower
      })
    }
    setAllCards((prev: any) => {
      if (!Array.isArray(prev)) return prev
      const byId = new Map<string, any>()
      prev.forEach((c: any) => {
        if (c?.id) byId.set(c.id, c)
      })
      let changed = false
      reduxAllCurrentUserCards.forEach((rc: any) => {
        if (!rc?.id) return
        const existing = byId.get(rc.id)
        const assignedToMe = isAssignedToMe(rc)
        if (existing) {
          // Card is already shown. If the redux version says the
          // current user is no longer assigned, drop it from the
          // homepage — homepage's filter contract is "tasks I'm
          // assigned to". Otherwise merge any field changes.
          if (!assignedToMe) {
            byId.delete(rc.id)
            changed = true
            return
          }
          if (
            existing === rc ||
            (existing.assignees === rc.assignees &&
              existing.status === rc.status &&
              existing.card_name === rc.card_name &&
              existing.due_date === rc.due_date &&
              existing.due_time === rc.due_time)
          ) {
            return
          }
          byId.set(rc.id, { ...existing, ...rc })
          changed = true
        } else if (assignedToMe) {
          // Newly assigned to current user — show it on the homepage
          // without waiting for the next full refetch.
          byId.set(rc.id, rc)
          changed = true
        }
      })
      return changed ? Array.from(byId.values()) : prev
    })
  }, [reduxAllCurrentUserCards, fullName])

  return { allCards, setAllCards, cardsHandler, homeFetchCardLoader, doneColumnLoader, setDoneColumnLoader }
}

export default useGetHomeCards